import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import {
  Loader2, RefreshCcw, ArrowLeft, AlertTriangle, Lightbulb,
  Target, Lock, Sparkles, Shield, Globe, Gauge, Crosshair,
  ClipboardList, Wrench, Brain, ClipboardCheck, RotateCcw, Upload,
  Copy, FileDown, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const SectionBlock = ({ icon: Icon, label, color, accent, children, testid }) => (
  <div className="flex gap-4" data-testid={testid}>
    <div
      className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: accent, color }}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0 flex-1 pt-1">
      <p className="eyebrow mb-1.5" style={{ color }}>{label}</p>
      <div className="text-slate-800 text-[15px] leading-relaxed">{children}</div>
    </div>
  </div>
);

const scoreTone = (score) => {
  if (score == null) return { color: "#475569", accent: "#F1F5F9", verdict: "—" };
  if (score >= 80) return { color: "#065F46", accent: "#D1FAE5", verdict: "Excellent" };
  if (score >= 65) return { color: "#1E40AF", accent: "#DBEAFE", verdict: "Solid" };
  if (score >= 50) return { color: "#B45309", accent: "#FEF3C7", verdict: "Work to do" };
  return { color: "#9F1239", accent: "#FFE4E6", verdict: "Needs rebuild" };
};

const MetricTile = ({ icon: Icon, label, value, sub, color, accent, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col"
  >
    <div className="flex items-center gap-2 mb-3">
      <span
        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
        style={{ backgroundColor: accent, color }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <p className="eyebrow" style={{ color }}>{label}</p>
    </div>
    <p className="font-display text-2xl font-bold text-slate-900 leading-tight">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1.5">{sub}</p>}
  </div>
);

export default function ResultsPage() {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/submissions/${id}`);
      setSubmission(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Defer first load to next microtask so setState inside load() never runs
    // synchronously inside the effect body (react-hooks/set-state-in-effect).
    const first = setTimeout(load, 0);
    const t = setInterval(load, 3000);
    return () => {
      clearTimeout(first);
      clearInterval(t);
    };
  }, [load]);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [packageInfo, setPackageInfo] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => {
      const pkg = data.packages.find((p) => p.id === "single_analysis");
      setPackageInfo({ ...pkg, enabled: data.enabled });
    }).catch(() => setPackageInfo(null));
  }, []);

  const startCheckout = async () => {
    if (!submission) return;
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "single_analysis",
        origin_url: window.location.origin,
        submission_id: submission.id,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout");
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-20 flex items-center justify-center" data-testid="results-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="results-error">
          <p className="text-slate-600 mb-4">{error}</p>
          <Link to="/" className="text-[#065F46] font-medium hover:underline">
            Upload a new swing
          </Link>
        </div>
      </div>
    );
  }

  // GDPR: the in-memory analysis cache has expired and the content is gone
  // forever from our servers. Show a friendly message and route the user to
  // run a fresh analysis.
  if (submission?.expired) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <main
          data-testid="results-expired"
          className="max-w-2xl mx-auto px-6 py-20 text-center"
        >
          <div className="w-14 h-14 rounded-xl bg-slate-100 mx-auto mb-4 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-slate-500" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
            This analysis is no longer available
          </h1>
          <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
            For privacy reasons we automatically delete analyses from our servers
            24 hours after upload. The content is now permanently gone. Upload a
            new swing video to get a fresh analysis.
          </p>
          <Link to="/">
            <Button
              data-testid="upload-new-after-expiry"
              className="bg-[#065F46] hover:bg-[#047857] text-white"
            >
              Upload a new swing video
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const created = new Date(submission.created_at).toLocaleString();
  const isReady = submission.status === "completed";
  const isAnalyzing = submission.status === "analyzing";
  const isPaid = submission.payment_status === "paid";
  const showPaywall = isReady && !isPaid && packageInfo?.enabled;
  const preview = submission.free_preview;
  const premium = submission.premium_analysis;
  const tone = scoreTone(preview?.swing_score);

  const buildPlainTextAnalysis = () => {
    const lines = ["SwingShape — Your swing analysis", "==================================", ""];
    if (preview) {
      lines.push(`Swing Score: ${preview.swing_score ?? "—"}/100`);
      lines.push(`Main fault: ${preview.main_fault || "—"}`);
      lines.push(`Ball-flight impact: ${preview.ball_flight_impact || "—"}`);
      lines.push("");
    }
    if (premium) {
      const block = (title, body) => {
        lines.push(title.toUpperCase());
        lines.push("-".repeat(title.length));
        lines.push(body);
        lines.push("");
      };
      if (premium.complete_analysis) block("Complete analysis", premium.complete_analysis);
      if (premium.why_it_happens) block("Why it happens", premium.why_it_happens);
      if (premium.how_to_fix) block("How to fix it", premium.how_to_fix);
      if (premium.next_practice_session?.length) {
        lines.push("YOUR NEXT PRACTICE SESSION (10-15 min)");
        lines.push("--------------------------------------");
        premium.next_practice_session.forEach((d, i) => {
          lines.push(`${i + 1}. ${d.name || "Drill"}${d.reps ? ` (${d.reps})` : ""}`);
          if (d.instruction) lines.push(`   ${d.instruction}`);
        });
        lines.push("");
      }
      if (premium.one_swing_thought) block("One swing thought", `"${premium.one_swing_thought}"`);
      if (premium.before_next_bucket?.length) {
        lines.push("BEFORE YOUR NEXT BUCKET OF BALLS");
        lines.push("--------------------------------");
        premium.before_next_bucket.forEach((c) => lines.push(`- ${c}`));
        lines.push("");
      }
      if (premium.next_upload_recommendation) block("Next upload recommendation", premium.next_upload_recommendation);
    }
    lines.push("Generated by SwingShape · swingshape.se");
    return lines.join("\n");
  };

  const copyAnalysisToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainTextAnalysis());
      toast.success("Analysis copied to clipboard");
    } catch {
      toast.error("Could not copy. Please select the text manually.");
    }
  };

  const downloadAnalysisAsPdf = () => {
    // Open the browser's native print dialog. Users can choose "Save as PDF"
    // in every modern browser. We attach a print-only stylesheet so the
    // generated PDF only contains the analysis (no header, no buttons).
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14" data-testid="results-main">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> New analysis
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <p className="eyebrow mb-2">Your analysis</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
              {isReady ? "Your swing breakdown" : isAnalyzing ? "Analyzing your swing…" : "Awaiting review"}
            </h1>
            <p className="text-xs text-slate-400 mt-3">Uploaded {created}</p>
          </div>
          <StatusBadge status={submission.status} />
        </div>

        {/* User's problem */}
        <Card className="border-slate-200 shadow-none p-5 mb-6 bg-white" data-testid="user-input-card">
          <p className="eyebrow mb-2">Your problem</p>
          <p className="text-slate-800 leading-relaxed text-[15px]" data-testid="user-input-display">
            {submission.user_input || <span className="text-slate-400 italic">None provided</span>}
          </p>
        </Card>

        {/* Pending state */}
        {!isReady && (
          <Card className="border-slate-200 shadow-none p-10 text-center" data-testid="feedback-pending">
            <div className="w-12 h-12 rounded-xl bg-blue-50 mx-auto mb-4 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
            <p className="font-display text-lg font-semibold text-slate-900 mb-1">
              {isAnalyzing ? "Analyzing your swing…" : "Queued for review"}
            </p>
            <p className="text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
              Our AI coach is studying the frames from your video. This usually takes under 30 seconds.
            </p>
            <Button
              data-testid="refresh-results-button"
              variant="ghost"
              size="sm"
              onClick={load}
              className="mt-5 text-slate-500 hover:text-slate-900"
            >
              <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </Card>
        )}

        {/* Free preview — shown as soon as analysis is ready */}
        {isReady && preview && (
          <Card
            className="border-slate-200 shadow-none p-6 sm:p-8 mb-6 relative overflow-hidden"
            data-testid="free-preview-card"
          >
            <div className="absolute top-5 right-5">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-[#065F46] border border-emerald-200">
                <Sparkles className="w-3 h-3" />
                Free preview
              </span>
            </div>
            <p className="eyebrow mb-1 text-[#065F46]">Quick read</p>
            <h2 className="font-display text-2xl font-semibold text-slate-900 mb-6">
              Here&apos;s what we spotted
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
              <MetricTile
                testid="metric-swing-score"
                icon={Gauge}
                label="Swing score"
                value={
                  <>
                    {preview.swing_score ?? "—"}
                    <span className="text-base font-medium text-slate-400">/100</span>
                  </>
                }
                sub={tone.verdict}
                color={tone.color}
                accent={tone.accent}
              />
              <MetricTile
                testid="metric-main-fault"
                icon={AlertTriangle}
                label="Main fault"
                value={
                  <span className="text-lg font-semibold leading-snug">
                    {preview.main_fault || "—"}
                  </span>
                }
                sub="Detail unlocked in full report"
                color="#B45309"
                accent="#FEF3C7"
              />
              <MetricTile
                testid="metric-ball-flight"
                icon={Crosshair}
                label="Ball flight impact"
                value={
                  <span className="text-base font-semibold leading-snug">
                    {preview.ball_flight_impact || "—"}
                  </span>
                }
                color="#1E40AF"
                accent="#DBEAFE"
              />
            </div>
          </Card>
        )}

        {/* Paywall — only if ready & unpaid & payments enabled */}
        {showPaywall && (
          <Card
            className="border-slate-200 shadow-none overflow-hidden p-0 bg-gradient-to-br from-white to-emerald-50/30"
            data-testid="paywall-card"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#065F46]" />
                <p className="eyebrow text-[#065F46]">Unlock the full breakdown</p>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">
                Your full coaching session is ready
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6 max-w-xl">
                You&apos;ve seen the score and the headline fault — unlock the complete analysis,
                <strong className="text-slate-900"> a 10-15 minute practice session</strong> built around your fault,
                <strong className="text-slate-900"> one swing thought</strong> for today,
                a quick <strong className="text-slate-900">pre-range checklist</strong>,
                and your coach&apos;s next move.
              </p>

              {/* Locked preview — blurred premium sections */}
              <div
                className="relative rounded-xl border border-slate-200 bg-white p-5 mb-6 overflow-hidden"
                data-testid="locked-preview"
              >
                <div className="space-y-4 select-none filter blur-[6px] pointer-events-none">
                  {[
                    { icon: ClipboardList, label: "Complete analysis", color: "#065F46", accent: "#D1FAE5" },
                    { icon: Lightbulb, label: "Why it happens", color: "#1E40AF", accent: "#DBEAFE" },
                    { icon: Wrench, label: "How to fix it", color: "#B45309", accent: "#FEF3C7" },
                    { icon: Target, label: "Your next practice session (10-15 min)", color: "#9F1239", accent: "#FFE4E6" },
                    { icon: Brain, label: "One swing thought", color: "#065F46", accent: "#D1FAE5" },
                    { icon: ClipboardCheck, label: "Before your next bucket of balls", color: "#1E40AF", accent: "#DBEAFE" },
                    { icon: Upload, label: "Next upload recommendation", color: "#B45309", accent: "#FEF3C7" },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: s.accent, color: s.color }}
                      >
                        <s.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.color }}>
                          {s.label}
                        </p>
                        <p className="text-sm text-slate-700">
                          Coaching detail revealed after payment.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/0 via-white/40 to-white/90">
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-900 text-white flex items-center justify-center">
                      <Lock className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-semibold text-slate-900">7 sections locked</p>
                  </div>
                </div>
              </div>

              {/* Price + CTA */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-3xl font-display font-bold text-slate-900 tracking-tight" data-testid="price-display">
                    ${packageInfo?.amount ?? "2.99"}{" "}
                    <span className="text-base font-medium text-slate-500">USD</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    One-time payment · Lifetime access to this analysis
                  </p>
                </div>
                <Button
                  data-testid="unlock-feedback-button"
                  onClick={startCheckout}
                  disabled={checkoutLoading}
                  className="h-12 px-6 bg-[#065F46] hover:bg-[#047857] text-white font-semibold text-sm shadow-sm"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to checkout…
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock Full Analysis - $2.99
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Trust strip */}
            <div className="border-t border-slate-200 px-6 sm:px-8 py-4 bg-white/50 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-slate-400" />
                Secure checkout by Stripe
              </span>
              <span className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-slate-400" />
                International cards accepted
              </span>
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                Apple Pay · Google Pay · Link
              </span>
            </div>
          </Card>
        )}

        {/* Premium analysis — only if paid */}
        {isReady && isPaid && premium && (
          <Card className="border-slate-200 shadow-none p-6 sm:p-8 mt-2 print-area" data-testid="premium-analysis-card">
            {/* GDPR notice — analysis is held in volatile memory and will
                disappear after the TTL. Encourage the user to save it. */}
            <div
              data-testid="gdpr-save-notice"
              className="no-print mb-6 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
            >
              <Trash2 className="w-4 h-4 mt-0.5 text-[#B45309] shrink-0" />
              <p className="text-xs text-slate-700 leading-relaxed">
                <strong>Save your analysis.</strong> For privacy reasons this analysis is
                automatically deleted from our servers 24 hours after upload &mdash; use the
                buttons below to copy it or download it as a PDF if you want to keep it.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <p className="eyebrow mb-1 text-[#065F46]">Full AI breakdown</p>
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                  Your complete game plan
                </h2>
              </div>
              <div className="flex items-center gap-2 no-print">
                <Button
                  data-testid="copy-analysis-button"
                  variant="outline"
                  size="sm"
                  onClick={copyAnalysisToClipboard}
                  className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-50"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </Button>
                <Button
                  data-testid="download-pdf-button"
                  variant="outline"
                  size="sm"
                  onClick={downloadAnalysisAsPdf}
                  className="gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-50"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  Download as PDF
                </Button>
                <Button
                  data-testid="refresh-results-button"
                  variant="ghost"
                  size="sm"
                  onClick={load}
                  className="text-slate-400 hover:text-slate-900"
                >
                  <RefreshCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              {premium.complete_analysis && (
                <SectionBlock
                  icon={ClipboardList}
                  label="Complete analysis"
                  color="#065F46"
                  accent="#D1FAE5"
                  testid="section-complete-analysis"
                >
                  <p className="my-1 first:mt-0">{premium.complete_analysis}</p>
                </SectionBlock>
              )}
              {premium.why_it_happens && (
                <SectionBlock
                  icon={Lightbulb}
                  label="Why the fault happens"
                  color="#1E40AF"
                  accent="#DBEAFE"
                  testid="section-why"
                >
                  <p className="my-1 first:mt-0">{premium.why_it_happens}</p>
                </SectionBlock>
              )}
              {premium.how_to_fix && (
                <SectionBlock
                  icon={Wrench}
                  label="How to fix it"
                  color="#B45309"
                  accent="#FEF3C7"
                  testid="section-how-to-fix"
                >
                  <p className="my-1 first:mt-0">{premium.how_to_fix}</p>
                </SectionBlock>
              )}
              {premium.next_practice_session && premium.next_practice_session.length > 0 && (
                <SectionBlock
                  icon={Target}
                  label="Your next practice session · 10-15 min"
                  color="#9F1239"
                  accent="#FFE4E6"
                  testid="section-next-practice"
                >
                  <ol className="space-y-3 my-1" data-testid="next-practice-list">
                    {premium.next_practice_session.map((drill, i) => (
                      <li
                        key={i}
                        className="rounded-lg border border-rose-100 bg-rose-50/40 p-3"
                        data-testid={`practice-drill-${i}`}
                      >
                        <div className="flex items-baseline justify-between gap-3 mb-1">
                          <p className="font-semibold text-slate-900 text-[15px]">
                            {drill.name || `Drill ${i + 1}`}
                          </p>
                          {drill.reps && (
                            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-[#9F1239] border border-rose-200">
                              {drill.reps}
                            </span>
                          )}
                        </div>
                        {drill.instruction && (
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {drill.instruction}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                </SectionBlock>
              )}
              {premium.one_swing_thought && (
                <SectionBlock
                  icon={Brain}
                  label="One swing thought"
                  color="#065F46"
                  accent="#D1FAE5"
                  testid="section-one-swing-thought"
                >
                  <p
                    className="my-1 first:mt-0 font-display text-lg font-semibold text-slate-900 leading-snug"
                    data-testid="one-swing-thought-text"
                  >
                    &ldquo;{premium.one_swing_thought}&rdquo;
                  </p>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Keep this one cue in your head — don&apos;t add anything else.
                  </p>
                </SectionBlock>
              )}
              {premium.before_next_bucket && premium.before_next_bucket.length > 0 && (
                <SectionBlock
                  icon={ClipboardCheck}
                  label="Before your next bucket of balls"
                  color="#1E40AF"
                  accent="#DBEAFE"
                  testid="section-before-next-bucket"
                >
                  <ul className="space-y-1.5 my-1" data-testid="before-bucket-list">
                    {premium.before_next_bucket.map((item, i) => (
                      <li
                        key={i}
                        data-testid={`bucket-check-${i}`}
                        className="flex items-start gap-2 text-[15px]"
                      >
                        <ClipboardCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#1E40AF]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </SectionBlock>
              )}
              {premium.next_upload_recommendation && (
                <SectionBlock
                  icon={Upload}
                  label="Next upload recommendation"
                  color="#B45309"
                  accent="#FEF3C7"
                  testid="section-next-upload"
                >
                  <p className="my-1 first:mt-0">{premium.next_upload_recommendation}</p>
                  <Link to="/" data-testid="upload-next-swing-cta">
                    <Button
                      variant="outline"
                      className="mt-3 border-[#B45309] text-[#B45309] hover:bg-amber-50 hover:text-[#B45309] gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Upload my next swing
                    </Button>
                  </Link>
                </SectionBlock>
              )}
            </div>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 mt-10" data-testid="footer-disclaimer">
          AI-powered golf swing analysis · Videos deleted after frame extraction · For educational purposes.
        </p>
      </main>
    </div>
  );
}
